import { BlogSection } from '@/data/blogPosts';
import { Button } from './ui/button';
import { ExternalLink } from 'lucide-react';

interface BlogContentProps {
  sections: BlogSection[];
}

export const BlogContent = ({ sections }: BlogContentProps) => {
  return (
    <article className="prose prose-lg prose-slate dark:prose-invert max-w-none">
      {sections.map((section, index) => {
        switch (section.type) {
          case 'hero':
            return (
              <p key={index} className="text-xl leading-relaxed text-muted-foreground first-letter:text-5xl first-letter:font-bold first-letter:text-primary first-letter:mr-1 first-letter:float-left mb-8">
                {section.content}
              </p>
            );

          case 'text':
            return (
              <div
                key={index}
                className="text-foreground/90 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: section.content || '' }}
              />
            );

          case 'heading':
            return (
              <h2 key={index} className="text-2xl font-bold text-foreground mt-12 mb-6 flex items-center gap-3">
                <span className="w-1 h-8 bg-primary rounded-full" />
                {section.title}
              </h2>
            );

          case 'list':
            return (
              <ul key={index} className="space-y-3 my-6">
                {section.items?.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-2 h-2 rounded-full bg-primary mt-2" />
                    <span className="text-foreground/90">{item}</span>
                  </li>
                ))}
              </ul>
            );

          case 'table':
            return (
              <div key={index} className="overflow-x-auto my-8 rounded-lg border border-border/50">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-muted/50">
                      {section.tableData?.headers.map((header, i) => (
                        <th
                          key={i}
                          className="px-6 py-4 text-left text-sm font-semibold text-foreground border-b border-border/50"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {section.tableData?.rows.map((row, i) => {
                      const isDiskover = row.App?.includes('Diskover');
                      return (
                        <tr
                          key={i}
                          className={`${
                            isDiskover ? 'bg-primary/5' : 'bg-card'
                          } border-b border-border/30 last:border-0`}
                        >
                          {section.tableData?.headers.map((header, j) => (
                            <td
                              key={j}
                              className={`px-6 py-4 text-sm ${
                                j === 0 ? 'font-semibold text-foreground' : 'text-foreground/80'
                              }`}
                            >
                              {row[header]}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );

          case 'apps':
            return (
              <div key={index} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 my-12">
                {section.apps?.map((app, i) => (
                  <a
                    key={i}
                    href={app.url}
                    target="_blank"
                    rel={app.name === 'Diskover' ? 'noopener' : 'noopener nofollow'}
                    className="group flex items-center gap-4 p-4 rounded-lg border border-border/50 bg-card/50 hover:bg-card hover:border-primary/50 transition-all duration-300"
                  >
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-muted flex items-center justify-center overflow-hidden">
                      <img
                        src={app.logo}
                        alt={`${app.name} logo`}
                        className="w-8 h-8 object-contain"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-foreground group-hover:text-primary transition-colors">
                        {app.name}
                      </div>
                      <div className="text-xs text-muted-foreground">Visit website</div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </a>
                ))}
              </div>
            );

          case 'pro-tip':
            return (
              <div key={index} className="my-6 p-6 rounded-lg bg-amber-50/80 dark:bg-amber-950/20 border-l-4 border-amber-400 dark:border-amber-600">
                <div className="flex items-start gap-3">
                  <span className="text-2xl flex-shrink-0">💡</span>
                  <div className="flex-1">
                    <strong className="text-amber-900 dark:text-amber-300 font-semibold">Personal Hack: </strong>
                    <span 
                      className="text-amber-800 dark:text-amber-200"
                      dangerouslySetInnerHTML={{ __html: section.content || '' }}
                    />
                  </div>
                </div>
              </div>
            );

          case 'images-grid':
            return (
              <div key={index} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 my-8">
                {section.images?.map((image, i) => (
                  <div key={i} className="relative overflow-hidden rounded-lg aspect-video group">
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            );

          case 'cta':
            return (
              <div key={index} className="my-12 p-8 rounded-xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20">
                <h3 className="text-2xl font-bold text-foreground mb-4">{section.content}</h3>
                <p className="text-muted-foreground mb-6">
                  Explore your city with AI-powered audio guides and discover hidden cultural gems at every corner.
                </p>
                <Button asChild size="lg" className="font-semibold">
                  <a href="/">Start Exploring with DiskOver</a>
                </Button>
              </div>
            );

          default:
            return null;
        }
      })}

      <style>{`
        .highlight {
          color: hsl(var(--primary));
          font-weight: 600;
        }
      `}</style>
    </article>
  );
};
